/**
 * OrgListTable: hierarchy-ordered list of all organizations, on the Carbon
 * DataTable (mockup 02).
 *
 * Rows are sorted to make the org tree implicit in the order:
 *   Platform → each MSP (alphabetical) → that MSP's Customers (alphabetical),
 *   then any orphan Customers (alphabetical) last. Customer rows are indented
 *   under their MSP parent. The status filter operates over the sorted list,
 *   so the tree shape is preserved within a filter.
 *
 * The action surface is context-sensitive (legacy mode):
 *   - PendingApproval → Approve + Reject (no Edit; admin should approve
 *     before tuning settings, so the row stays consistent through the queue).
 *   - Active           → Edit + Deactivate.
 *   - Inactive         → Edit only (deactivating an already-inactive row is
 *     a no-op and would confuse the admit log; re-activation happens via
 *     approve only when status is PendingApproval).
 *   - Rejected         → Edit only, same rationale as Inactive.
 *   - Platform type    → Edit only; deactivate is blocked server-side anyway.
 *
 * Below 672px the DataTable stacks each row into a block, so the table never
 * widens the page on a phone.
 */

import { useMemo, useState, type ReactNode } from 'react'

import type { OrganizationListItemDto } from '@/lib/api'
import { Button } from '@/components/carbon/Button'
import { ContainedTabs } from '@/components/carbon/ContainedTabs'
import { DataTable, TableItem, type DataColumn } from '@/components/carbon/DataTable'
import { OverflowMenu, type OverflowMenuItem } from '@/components/carbon/OverflowMenu'
import { Tag } from '@/components/carbon/Tag'
import PivotChip from '@/components/AdminPortal/PivotChip'
import { countLabel } from '@/components/AdminPortal/admin-format'

export type OrgFilterChip = 'all' | 'active' | 'inactive' | 'pending'

/**
 * The table is dual-mode:
 *
 *   - Legacy mode (`/organization-management` page): callers supply
 *     `onEdit / onDeactivate / onApprove / onReject` and the per-row overflow
 *     menu renders, plus the table's own status tabs.
 *
 *   - Portal mode (`/admin?tab=organizations`): callers supply `onOpen` and
 *     `onPivot`. The row opens the right-side drawer (a click, Enter, or the
 *     "Open" button), and the Tenants / People counts become PivotChips that
 *     cross-navigate to those tabs. The parent owns the status filter and
 *     passes its tabs through `tabs`.
 *
 * Mode selection: `onOpen` is the discriminator. When defined, portal mode.
 * When undefined, legacy mode. `onPivot` is required alongside `onOpen` so
 * the count cells stay actionable; if it's missing we degrade to plain text
 * counts rather than rendering dead chips.
 */
interface Props {
  rows: OrganizationListItemDto[]
  /** Legacy per-row action; optional so portal-mode callers can omit it. */
  onEdit?: (row: OrganizationListItemDto) => void
  /** Legacy per-row action; optional so portal-mode callers can omit it. */
  onDeactivate?: (row: OrganizationListItemDto) => void
  /** Legacy per-row action; optional so portal-mode callers can omit it. */
  onApprove?: (row: OrganizationListItemDto) => void
  /** Legacy per-row action; optional so portal-mode callers can omit it. */
  onReject?: (row: OrganizationListItemDto) => void
  /** Portal mode: open the right-side drawer for `row`. */
  onOpen?: (row: OrganizationListItemDto) => void
  /** Portal mode: cross-tab pivot to People / Tenants scoped to `orgId`. */
  onPivot?: (target: 'tenants' | 'people', orgId: string) => void
  /** The table title (and accessible name). Default "Organizations". */
  title?: string
  /** One line under the title. */
  description?: ReactNode
  /** Portal mode: the parent's status tabs (FilterChipRow). */
  tabs?: ReactNode
  /** Toolbar contents (the "Add organization" button). */
  toolbar?: ReactNode
  /** Skeleton rows while the list loads. */
  isLoading?: boolean
  /** The empty row's content. Default "No organizations match this filter." */
  empty?: ReactNode
}

const FILTER_LABEL: Record<OrgFilterChip, string> = {
  all: 'All',
  active: 'Active',
  inactive: 'Inactive',
  pending: 'Pending',
}

type SortedRow = OrganizationListItemDto & { depth: number }

/**
 * Status as a Carbon tag (the Neutral Bad State Rule: no hue). Pending
 * approval is the state that needs a decision, so it takes the high-contrast
 * neutral; inactive and rejected rows recede to the outline tag.
 */
function StatusTag({ status }: { status: string }) {
  if (status === 'PendingApproval') return <Tag kind="high-contrast">Pending</Tag>
  if (status === 'Inactive' || status === 'Rejected') return <Tag kind="outline">{status}</Tag>
  return <Tag>{status}</Tag>
}

/** The Tenants and People count columns (portal mode). */
const COUNT_WIDTH = '7rem'

/**
 * A count under a header that names its noun. Below c-md the DataTable
 * hides the header row and stacks the cells, so the noun comes back there.
 */
function PhoneNoun({ n, one, many }: { n: number; one: string; many: string }) {
  return (
    <span className="tabular-nums">
      {n}
      <span className="c-md:hidden"> {n === 1 ? one : many}</span>
    </span>
  )
}

function truncateTid(tid: string | null | undefined): string {
  if (!tid) return '—'
  if (tid.length <= 13) return tid
  return `${tid.slice(0, 8)}…${tid.slice(-4)}`
}

export default function OrgListTable({
  rows,
  onEdit,
  onDeactivate,
  onApprove,
  onReject,
  onOpen,
  onPivot,
  title = 'Organizations',
  description,
  tabs,
  toolbar,
  isLoading,
  empty,
}: Props) {
  const [filter, setFilter] = useState<OrgFilterChip>('all')
  // Portal mode = the parent owns filtering + drawer routing. We branch on
  // `onOpen` (the discriminator) so the row-level rendering can stay clean.
  const isPortalMode = onOpen !== undefined

  // Parent-name lookup: keyed by org Id so we can resolve the parent's display
  // name without burdening the caller. Built once per `rows` identity change.
  const parentNameById = useMemo(() => {
    const map = new Map<string, string>()
    for (const r of rows) map.set(r.id, r.name)
    return map
  }, [rows])

  // Sorted, tree-flattened list: Platform first, then each MSP (alphabetical)
  // followed immediately by its Customer children (alphabetical), then any
  // orphan Customers last. The `depth` flag lets the row indent children
  // without re-deriving the hierarchy.
  const sorted = useMemo(() => {
    const byName = (a: OrganizationListItemDto, b: OrganizationListItemDto) =>
      a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })

    const platforms: OrganizationListItemDto[] = []
    const msps: OrganizationListItemDto[] = []
    const customers: OrganizationListItemDto[] = []
    const others: OrganizationListItemDto[] = []
    for (const r of rows) {
      if (r.type === 'Platform') platforms.push(r)
      else if (r.type === 'MSP') msps.push(r)
      else if (r.type === 'Customer') customers.push(r)
      else others.push(r) // Defensive: server types `type` as open string.
    }
    platforms.sort(byName); msps.sort(byName); customers.sort(byName); others.sort(byName)

    // Bucket Customers under their parent MSP. A Customer whose parent isn't
    // in the current rows array (filtered out, or genuinely missing) is
    // surfaced at the bottom rather than hidden.
    const customersByParent = new Map<string, OrganizationListItemDto[]>()
    const orphanCustomers: OrganizationListItemDto[] = []
    for (const c of customers) {
      const parentId = c.parentOrganizationId
      if (parentId && parentNameById.has(parentId)) {
        const list = customersByParent.get(parentId) ?? []
        list.push(c)
        customersByParent.set(parentId, list)
      } else {
        orphanCustomers.push(c)
      }
    }

    const out: SortedRow[] = []
    for (const p of platforms) out.push({ ...p, depth: 0 })
    for (const m of msps) {
      out.push({ ...m, depth: 0 })
      const children = customersByParent.get(m.id) ?? []
      for (const c of children) out.push({ ...c, depth: 1 })
    }
    for (const c of orphanCustomers) out.push({ ...c, depth: 0 })
    for (const o of others) out.push({ ...o, depth: 0 })
    return out
  }, [rows, parentNameById])

  const filtered = useMemo(() => {
    if (isPortalMode || filter === 'all') return sorted
    if (filter === 'active') return sorted.filter(o => o.status === 'Active')
    if (filter === 'inactive') return sorted.filter(o => o.status === 'Inactive' || o.status === 'Rejected')
    return sorted.filter(o => o.status === 'PendingApproval')
  }, [sorted, filter, isPortalMode])

  // Counts feed the legacy tab tags: admins can read the queue depth without
  // switching tabs. Computed in one pass for clarity.
  const counts = useMemo(() => ({
    all: rows.length,
    active: rows.filter(o => o.status === 'Active').length,
    inactive: rows.filter(o => o.status === 'Inactive' || o.status === 'Rejected').length,
    pending: rows.filter(o => o.status === 'PendingApproval').length,
  }), [rows])

  const parentOf = (row: OrganizationListItemDto) =>
    row.parentOrganizationId ? parentNameById.get(row.parentOrganizationId) ?? null : null

  const columns: DataColumn<SortedRow>[] = [
    {
      id: 'name',
      header: 'Name',
      primary: true,
      cell: row => (
        <span className={row.depth > 0 ? 'flex items-baseline gap-2 pl-4' : 'block'}>
          {row.depth > 0 ? <span aria-hidden="true" className="text-text-helper">└</span> : null}
          <span className="block min-w-0">
            <TableItem title={row.name} sub={<span className="font-mono">{row.slug}</span>} />
          </span>
        </span>
      ),
    },
    {
      id: 'parent',
      header: 'Parent',
      inlineOnPhone: true,
      cell: row => parentOf(row) ?? <span className="text-text-helper">No parent</span>,
    },
    { id: 'type', header: 'Type', inlineOnPhone: true, cell: row => row.type },
    { id: 'status', header: 'Status', cell: row => <StatusTag status={row.status} /> },
    {
      id: 'tid',
      header: 'Directory ID',
      hideBelow: 'c-lg',
      cell: row => (
        <span className="font-mono text-helper" title={row.azureAdTenantId ?? undefined}>
          {truncateTid(row.azureAdTenantId)}
        </span>
      ),
    },
  ]

  if (isPortalMode) {
    columns.push(
      {
        // Tenants pivot: the count becomes an actionable chip that
        // cross-navigates to the Monitored Tenants tab pre-scoped to this org.
        // The header names the noun, so the chip shows the bare count (it
        // truncated to "0 ten…" with the noun); the width keeps a 3-digit
        // count and its arrow whole.
        id: 'tenants',
        header: 'Tenants',
        width: COUNT_WIDTH,
        cell: row =>
          onPivot ? (
            <PivotChip
              label={countLabel(row.linkedTenantCount, 'tenant', 'tenants')}
              display={<PhoneNoun n={row.linkedTenantCount} one="tenant" many="tenants" />}
              onClick={() => onPivot('tenants', row.id)}
              ariaLabel={`View ${countLabel(row.linkedTenantCount, 'tenant', 'tenants')} linked to ${row.name}`}
            />
          ) : (
            <span className="tabular-nums">{row.linkedTenantCount}</span>
          ),
      },
      {
        // People pivot: same shape, lands on the People tab.
        id: 'people',
        header: 'People',
        width: COUNT_WIDTH,
        cell: row =>
          onPivot ? (
            <PivotChip
              label={countLabel(row.linkedUserCount, 'person', 'people')}
              display={<PhoneNoun n={row.linkedUserCount} one="person" many="people" />}
              onClick={() => onPivot('people', row.id)}
              ariaLabel={`View ${countLabel(row.linkedUserCount, 'person', 'people')} linked to ${row.name}`}
            />
          ) : (
            <span className="tabular-nums">{row.linkedUserCount}</span>
          ),
      },
      {
        // Open: the single per-row action; every detail, edit and approve
        // flow lives in the right-side drawer the parent owns.
        id: 'open',
        header: <span className="sr-only">Open</span>,
        align: 'end',
        cell: row => (
          <Button kind="ghost" size="sm" onClick={() => onOpen?.(row)} aria-label={`Open ${row.name}`}>
            Open
          </Button>
        ),
      },
    )
  } else {
    columns.push(
      { id: 'users', header: 'Users', align: 'end', cell: row => <span className="tabular-nums">{row.linkedUserCount}</span> },
      { id: 'envs', header: 'Envs', align: 'end', cell: row => <span className="tabular-nums">{row.linkedTenantCount}</span> },
      {
        id: 'actions',
        header: <span className="sr-only">Actions</span>,
        align: 'end',
        cell: row => {
          const items = legacyActions(row, { onEdit, onDeactivate, onApprove, onReject })
          return items.length > 0 ? <OverflowMenu label={`Actions for ${row.name}`} items={items} size="sm" /> : null
        },
      },
    )
  }

  const legacyTabs = !isPortalMode ? (
    <ContainedTabs
      aria-label="Filter by status"
      value={filter}
      onChange={id => setFilter(id as OrgFilterChip)}
      tabs={(['all', 'active', 'pending', 'inactive'] as OrgFilterChip[]).map(chip => ({
        id: chip,
        label: FILTER_LABEL[chip],
        count: counts[chip],
      }))}
    />
  ) : null

  return (
    <DataTable
      title={title}
      description={description}
      columns={columns}
      rows={filtered}
      rowId={row => row.id}
      rowLabel={row => row.name}
      tabs={tabs ?? legacyTabs}
      toolbar={toolbar}
      isLoading={isLoading}
      onRowActivate={isPortalMode ? row => onOpen?.(row) : undefined}
      empty={empty ?? 'No organizations match this filter.'}
    />
  )
}

function legacyActions(
  row: OrganizationListItemDto,
  handlers: {
    onEdit?: (row: OrganizationListItemDto) => void
    onDeactivate?: (row: OrganizationListItemDto) => void
    onApprove?: (row: OrganizationListItemDto) => void
    onReject?: (row: OrganizationListItemDto) => void
  },
): OverflowMenuItem[] {
  const isPlatform = row.type === 'Platform'
  const isPending = row.status === 'PendingApproval'
  const isInactive = row.status === 'Inactive' || row.status === 'Rejected'
  const items: OverflowMenuItem[] = []
  if (isPending) {
    if (handlers.onApprove) items.push({ id: 'approve', label: 'Approve', onClick: () => handlers.onApprove?.(row) })
    if (handlers.onReject) items.push({ id: 'reject', label: 'Reject', danger: true, onClick: () => handlers.onReject?.(row) })
    return items
  }
  if (handlers.onEdit) items.push({ id: 'edit', label: 'Edit', onClick: () => handlers.onEdit?.(row) })
  if (!isInactive && !isPlatform && handlers.onDeactivate) {
    items.push({ id: 'deactivate', label: 'Deactivate', danger: true, onClick: () => handlers.onDeactivate?.(row) })
  }
  return items
}
